/* eslint-disable react/prefer-stateless-function */
import { Spinner } from '@schibsted-svp/react-ui';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { removeDuplicateLiveStreamInfo } from 'store/live/actions';
import { getDuplicatedAssetId, isLiveStreamDuplicating } from 'store/live/selectors';

import LiveAssetsList from 'components/LiveAssetsList/LiveAssetsList';
import ScreenContainer from 'components/ui/ScreenContainer';

import css from './LiveScreen.module.scss';

const cln = classnames.bind(css);

function LiveScreen({
    isFetching = false,
    readyToPublishAssets = [],
    wasLiveAssets = [],
    currentLiveAssets = [],
    upcomingLiveAssets = [],
    personalLiveAssets = [],
    fetchLiveAssets,
    fetchLiveEncoders,
    fetchWasLiveAssets,
    newsroom,
    liveErrorMessage,
    wasLiveErrorMessage,
}) {
    const { tab = 'video' } = useParams();

    useEffect(() => {
        fetchLiveAssets({ provider: newsroom, assetType: tab });
    }, [fetchLiveAssets, newsroom, tab]);

    useEffect(() => {
        fetchLiveEncoders(newsroom);
    }, [fetchLiveEncoders, newsroom]);

    useEffect(() => {
        fetchWasLiveAssets({ provider: newsroom, assetType: tab });
    }, [fetchWasLiveAssets, newsroom, tab]);

    const isDuplicating = useSelector(isLiveStreamDuplicating);
    const duplicatedAssetId = useSelector(getDuplicatedAssetId);
    const history = useHistory();

    const dispatch = useDispatch();

    useEffect(() => {
        if (duplicatedAssetId && !isDuplicating) {
            history.push(`/${newsroom}/assets/${duplicatedAssetId}`);
            dispatch(removeDuplicateLiveStreamInfo());
        }
    }, [duplicatedAssetId, isDuplicating, dispatch, history, newsroom]);

    if (isDuplicating) {
        return (
            <ScreenContainer>
                <Spinner containerClassName={css.spinner} />
                <p className={css.text}>Duplication in progress...</p>
            </ScreenContainer>
        );
    }

    if (isFetching) {
        return (
            <ScreenContainer>
                <Spinner containerClassName={css.spinner} />
            </ScreenContainer>
        );
    }

    const streamTypes = ['video', 'audio'];

    const onLiveTypeChange = (type) => {
        fetchLiveAssets({ provider: newsroom, assetType: type });
        fetchWasLiveAssets({ provider: newsroom, assetType: type });
    };

    return (
        <ScreenContainer>
            <div className={css.filters}>
                <ul className={css.streamTypesContainer}>
                    {streamTypes.map((type) => (
                        <NavLink to={`/${newsroom}/live/${type}`} key={type}>
                            <li
                                className={cln(css.streamTypeFilter, {
                                    'streamTypeFilter--active': tab === type,
                                })}
                                onClick={() => onLiveTypeChange(type)}
                            >
                                {type}
                            </li>
                        </NavLink>
                    ))}
                </ul>
            </div>
            {personalLiveAssets.length !== 0 && (
                <LiveAssetsList
                    header="My live videos"
                    visibleIcons={['date', 'views', 'duration', 'endTime']}
                    assets={personalLiveAssets}
                    errorMessage={liveErrorMessage}
                    fetchAssets={fetchLiveAssets}
                    newsroom={newsroom}
                    className={css['personal-lives']}
                />
            )}
            <div className={css['live-screen']}>
                <div className={css['live-and-was-live']}>
                    <div className={css['live-videos']}>
                        <LiveAssetsList
                            header="Current live videos"
                            visibleIcons={['date', 'views', 'duration', 'endTime']}
                            assets={currentLiveAssets}
                            errorMessage={liveErrorMessage}
                            fetchAssets={fetchLiveAssets}
                            newsroom={newsroom}
                            className={css['current-live']}
                        />
                        <LiveAssetsList
                            header="Upcoming live videos"
                            visibleIcons={['date', 'views', 'duration', 'endTime']}
                            assets={upcomingLiveAssets}
                            errorMessage={liveErrorMessage}
                            fetchAssets={fetchLiveAssets}
                            newsroom={newsroom}
                        />
                    </div>
                    <LiveAssetsList
                        header="Was live (last 24 hours)"
                        visibleIcons={['date', 'views', 'duration', 'endTime']}
                        assets={wasLiveAssets}
                        errorMessage={wasLiveErrorMessage}
                        fetchAssets={fetchWasLiveAssets}
                        newsroom={newsroom}
                    />
                </div>
                <LiveAssetsList
                    header="Ready to be published"
                    visibleIcons={[]}
                    assets={readyToPublishAssets}
                    errorMessage={liveErrorMessage}
                    fetchAssets={fetchLiveAssets}
                    newsroom={newsroom}
                />
            </div>
        </ScreenContainer>
    );
}

LiveScreen.propTypes = {
    currentLiveAssets: PropTypes.arrayOf(PropTypes.object),
    upcomingLiveAssets: PropTypes.arrayOf(PropTypes.object),
    readyToPublishAssets: PropTypes.arrayOf(PropTypes.object),
    wasLiveAssets: PropTypes.arrayOf(PropTypes.object),
    personalLiveAssets: PropTypes.arrayOf(PropTypes.object),
    fetchLiveAssets: PropTypes.func.isRequired,
    fetchLiveEncoders: PropTypes.func.isRequired,
    fetchWasLiveAssets: PropTypes.func.isRequired,
    newsroom: PropTypes.string.isRequired,
    isFetching: PropTypes.bool,
    liveErrorMessage: PropTypes.string,
    wasLiveErrorMessage: PropTypes.string,
};

export default LiveScreen;
